import { getStableWidgetLocale } from '../utils/locale';
const NOISE_PATTERN = /(cookie|consent|privacy|banner|modal|dialog|footer|header|nav|menu|sidebar|newsletter|social|share)/i;
const NOISE_CONTAINER_SELECTOR = 'nav, header, footer, aside, dialog, [role="navigation"], [role="banner"], [role="contentinfo"], [aria-modal="true"]';
const CTA_SELECTOR = 'button, a, [role="button"], input[type="button"], input[type="submit"]';
const CANDIDATE_SELECTOR = 'main, article, [role="main"], section, div';
export function buildDynamicWidgetPromptRequest(pageUrl = window.location.href) {
    const promptUrl = resolvePromptPageUrl(document, pageUrl);
    const page = extractPageContext(document, promptUrl);
    const contextItems = extractInjectedContext(window?.shineSettings?.dynamicWidgetContext);
    const locale = getStableWidgetLocale() || page.locale;
    const fingerprint = hashString(JSON.stringify({ locale, page, contextItems }));
    return {
        url: promptUrl,
        fingerprint,
        locale,
        page,
        contextItems,
    };
}
function resolvePromptPageUrl(doc, pageUrl) {
    const links = Array.from(doc.querySelectorAll('link[rel~="canonical" i]'));
    // Ambiguous declarations must not merge unrelated pages into one experiment.
    if (links.length !== 1)
        return pageUrl;
    const href = links[0].getAttribute('href')?.trim();
    if (!href)
        return pageUrl;
    try {
        const current = new URL(pageUrl);
        const canonical = new URL(href, current);
        if (!['http:', 'https:'].includes(canonical.protocol) ||
            canonical.origin !== current.origin || canonical.username || canonical.password)
            return pageUrl;
        canonical.search = '';
        canonical.hash = '';
        return canonical.href;
    }
    catch {
        return pageUrl;
    }
}
export function extractPageContext(doc, pageUrl) {
    const title = sanitizeText(doc.title, 160);
    const description = sanitizeText(doc.querySelector('meta[name="description"]')?.getAttribute('content') || '', 220);
    const locale = sanitizeText(doc.documentElement?.lang || '', 32);
    const contentHeadings = Array.from(doc.querySelectorAll('h1, h2, h3'))
        .filter((el) => !isInsideNoiseContainer(el) && isProbablyVisible(el))
        .map((el) => sanitizeText(el.textContent || '', 120))
        .filter(Boolean)
        .filter(unique());
    const primaryHeading = sanitizeText(contentHeadings[0] || '', 160);
    const urlSegments = extractUrlSegments(pageUrl);
    const headings = contentHeadings.slice(0, 8);
    const keywordTokens = tokenize([title, primaryHeading, ...urlSegments].join(' '));
    const highlights = extractHighlights(doc, keywordTokens);
    const ctas = Array.from(doc.querySelectorAll(CTA_SELECTOR))
        .filter((el) => !isInsideNoiseContainer(el) && isProbablyVisible(el))
        .map((el) => sanitizeText(el.value || el.textContent || '', 80))
        .filter((text) => text.length >= 2 && !NOISE_PATTERN.test(text))
        .filter(unique())
        .slice(0, 8);
    const summary = sanitizeText(highlights.slice(0, 2).join(' '), 320);
    return {
        title,
        description,
        locale,
        primaryHeading,
        summary,
        urlSegments,
        headings,
        highlights,
        ctas,
    };
}
export function extractInjectedContext(input) {
    if (typeof input === 'string') {
        const value = sanitizeText(input, 160);
        return value ? [{ label: 'context', value }] : [];
    }
    if (Array.isArray(input)) {
        return input
            .map((value, index) => ({
            label: `context_${index + 1}`,
            value: sanitizeUnknownValue(value),
        }))
            .filter((item) => item.value.length > 0)
            .slice(0, 8);
    }
    if (input && typeof input === 'object') {
        return Object.entries(input)
            .map(([label, value]) => ({
            label: sanitizeText(label, 48),
            value: sanitizeUnknownValue(value),
        }))
            .filter((item) => item.label.length > 0 && item.value.length > 0)
            .slice(0, 8);
    }
    return [];
}
function extractHighlights(doc, keywordTokens) {
    const blocks = Array.from(doc.querySelectorAll(CANDIDATE_SELECTOR))
        .map((element) => scoreBlock(element, keywordTokens))
        .filter((block) => block !== null)
        .sort((left, right) => right.score - left.score);
    const selected = [];
    for (const block of blocks) {
        const overlaps = selected.some((existing) => existing.element.contains(block.element) || block.element.contains(existing.element));
        if (overlaps)
            continue;
        selected.push(block);
        if (selected.length >= 5)
            break;
    }
    return selected
        .map((block) => {
        const excerpt = takeExcerpt(block.text, 220);
        return block.heading ? sanitizeText(`${block.heading}: ${excerpt}`, 220) : excerpt;
    })
        .filter(Boolean);
}
function scoreBlock(element, keywordTokens) {
    if (!isProbablyVisible(element))
        return null;
    if (isInsideNoiseContainer(element))
        return null;
    const hint = `${element.tagName} ${element.id} ${element.className}`.trim();
    if (NOISE_PATTERN.test(hint))
        return null;
    const text = sanitizeText(getContentText(element), 900);
    if (text.length < 70)
        return null;
    let score = Math.min(text.length, 700) / 80;
    const tag = element.tagName.toLowerCase();
    if (tag === 'main' || tag === 'article')
        score += 4;
    if (tag === 'section')
        score += 2;
    if (element.querySelector('h1, h2, h3'))
        score += 2;
    const linkTextLength = Array.from(element.querySelectorAll('a'))
        .map((link) => sanitizeText(link.textContent || '', 120).length)
        .reduce((sum, length) => sum + length, 0);
    const linkDensity = text.length > 0 ? linkTextLength / text.length : 0;
    if (linkDensity > 0.55)
        score -= 4;
    if (keywordTokens.some((token) => text.toLowerCase().includes(token))) {
        score += 2;
    }
    const heading = Array.from(element.querySelectorAll('h1, h2, h3'))
        .filter((el) => !isInsideNoiseContainer(el))
        .map((el) => sanitizeText(el.textContent || '', 100))
        .find(Boolean) || '';
    return {
        element,
        text,
        heading,
        score,
    };
}
function isProbablyVisible(element) {
    const htmlElement = element;
    if (htmlElement.hidden)
        return false;
    if (htmlElement.getAttribute('aria-hidden') === 'true')
        return false;
    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        const style = window.getComputedStyle(htmlElement);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
    }
    return true;
}
function isInsideNoiseContainer(element) {
    if (isNoiseContainer(element))
        return true;
    const parent = element.parentElement?.closest(NOISE_CONTAINER_SELECTOR);
    if (parent)
        return true;
    let current = element.parentElement;
    while (current && current !== document.body) {
        if (isNoiseContainer(current))
            return true;
        current = current.parentElement;
    }
    return false;
}
function isNoiseContainer(element) {
    const tag = element.tagName.toLowerCase();
    if (['nav', 'header', 'footer', 'aside', 'dialog'].includes(tag))
        return true;
    const role = element.getAttribute('role') || '';
    if (/^(navigation|banner|contentinfo)$/i.test(role))
        return true;
    if (element.getAttribute('aria-modal') === 'true')
        return true;
    const hint = `${element.id} ${element.className}`.trim();
    return NOISE_PATTERN.test(hint);
}
function getContentText(element) {
    const clone = element.cloneNode(true);
    Array.from(clone.querySelectorAll(NOISE_CONTAINER_SELECTOR)).forEach((node) => node.remove());
    Array.from(clone.querySelectorAll('*')).forEach((node) => {
        if (isNoiseContainer(node))
            node.remove();
    });
    return clone.textContent || '';
}
function takeExcerpt(text, maxLength) {
    if (text.length <= maxLength)
        return text;
    const sentences = text.split(/(?<=[.!?])\s+/);
    let result = '';
    for (const sentence of sentences) {
        if ((result + ' ' + sentence).trim().length > maxLength)
            break;
        result = `${result} ${sentence}`.trim();
    }
    if (result.length >= 40)
        return result;
    return text.slice(0, maxLength).trim();
}
function extractUrlSegments(pageUrl) {
    try {
        const url = new URL(pageUrl, window.location.origin);
        return url.pathname
            .split('/')
            .map((segment) => decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim())
            .filter(Boolean)
            .filter(unique())
            .slice(0, 6)
            .map((segment) => sanitizeText(segment, 80));
    }
    catch {
        return [];
    }
}
function sanitizeUnknownValue(value) {
    if (typeof value === 'string')
        return sanitizeText(value, 160);
    if (typeof value === 'number' || typeof value === 'boolean')
        return sanitizeText(String(value), 160);
    if (value == null)
        return '';
    try {
        return sanitizeText(JSON.stringify(value), 160);
    }
    catch {
        return '';
    }
}
function sanitizeText(value, maxLength) {
    return value
        .replace(/<[^>]+>/g, ' ')
        .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[redacted]')
        .replace(/\b(?:\+?\d[\d\s().-]{6,}\d)\b/g, '[redacted]')
        .replace(/\b\d{7,}\b/g, '[redacted]')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, maxLength)
        .trim();
}
function tokenize(text) {
    return text
        .toLowerCase()
        .split(/[^a-z0-9]+/i)
        .map((token) => token.trim())
        .filter((token) => token.length >= 4)
        .filter(unique())
        .slice(0, 12);
}
function hashString(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
}
function unique() {
    return (value, index, array) => array.indexOf(value) === index;
}
